import {Belt_samplesPerSecond} from "../../../../../UI/@Shared/Processors/BreathProcessor.js";

export interface Peak {
	sampleIndex: number;
	sample: BreathSample;
	prominences: PeakProminences;
}

function GetArrayMinAndMax(array: number[]) {
	if (array.length === 0) return null;
	let min = array[0];
	let max = array[0];
	for (const value of array) {
		if (value < min) min = value;
		if (value > max) max = value;
	}
	return {min, max};
}

export type BreathSample = {value: number, time: number};

export function DetectBreathingPeaks(samples: BreathSample[], opt: {
	minProminence_rel: number; // Example: 0.1 (10% of signal range -- with signal-range being local to the "window" around the given possible-crest)
	minProminence_abs: number;
	minDistBetweenPeaksMs: number; // Suggestion: 1000ms
	windowSizeMs: number, // Suggestion: 20000ms (20 seconds)
}): Peak[] {
	const windowSize_samples = ((opt.windowSizeMs / 1000) * Belt_samplesPerSecond).RoundTo(1);

	// Find all local maxima
	const peaks: Peak[] = [];
	for (let i = 1; i < samples.length - 1; i++) {
		if (isLocalMaximumSimple(samples, i)) {
			//const prominence = calculateProminence(samples, i);
			const prominences = CalculateProminencesForPeak(samples, i, windowSize_samples);
			peaks.push({sampleIndex: i, sample: samples[i], prominences});
		}
	}

	// Filter by prominence
	const significantPeaks = peaks.filter(p=>p.prominences.rel >= opt.minProminence_rel && p.prominences.abs >= opt.minProminence_abs);

	// Apply non-maximum suppression, but be gentle with edge cases
	const finalPeaks: Peak[] = [];
	for (const peak of significantPeaks.OrderByDescending(a=>a.prominences.rel)) {
		const tooClose = finalPeaks.some(existing=>existing.sample.time.Distance(peak.sample.time) < opt.minDistBetweenPeaksMs);
		if (!tooClose) {
			finalPeaks.push(peak);
		}
	}
	return finalPeaks.OrderBy(a=>a.sampleIndex);
}

function isLocalMaximumSimple(samples: BreathSample[], index: number): boolean {
	const sample = samples[index];

	// Look for the nearest non-equal values on both sides
	let leftValue = sample.value;
	let rightValue = sample.value;

	// Find nearest different value to the left
	for (let i = index - 1; i >= 0; i--) {
		if (samples[i].value != sample.value) {
			leftValue = samples[i].value;
			break;
		}
	}

	// Find nearest different value to the right
	for (let i = index + 1; i < samples.length; i++) {
		if (samples[i].value !== sample.value) {
			rightValue = samples[i].value;
			break;
		}
	}

	// It's a maximum if the value is greater than both neighboring different values
	return sample.value > leftValue && sample.value > rightValue;
}

export type PeakProminences = {
	abs: number;
	abs_left: number;
	abs_right: number;
	rel: number;
}
function CalculateProminencesForPeak(samples: BreathSample[], peakIndex: number, windowSize_samples: number): PeakProminences {
	const start = (peakIndex - (windowSize_samples - 1)).KeepAtLeast(0);
	const end = (peakIndex + 1).KeepAtMost(samples.length);
	const localWindow = samples.slice(start, end);
	const localMinMax = GetArrayMinAndMax(localWindow.map(a=>a.value));
	if (!localMinMax) return {abs: 0, abs_left: 0, abs_right: 0, rel: 0};
	const localRange = localMinMax.max - localMinMax.min;

	const prominences_abs = CalculateProminencesForPeak_Absolute(samples, peakIndex);
	const prominence_rel = prominences_abs.prominence_combined / localRange; // Normalized prominence
	return {
		abs: prominences_abs.prominence_combined,
		abs_left: prominences_abs.prominence_left,
		abs_right: prominences_abs.prominence_right,
		rel: prominence_rel,
	};
}

function CalculateProminencesForPeak_Absolute(samples: BreathSample[], peakIndex: number) {
	const peakValue = samples[peakIndex].value;

	// Find the lowest point to the left
	let leftMin = peakValue;
	for (let i = peakIndex - 1; i >= 0; i--) {
		leftMin = Math.min(leftMin, samples[i].value);
		if (samples[i].value > peakValue) break; // Hit a higher peak
	}

	// Find the lowest point to the right
	let rightMin = peakValue;
	for (let i = peakIndex + 1; i < samples.length; i++) {
		rightMin = Math.min(rightMin, samples[i].value);
		if (samples[i].value > peakValue) break; // Hit a higher peak
	}

	const prominence_left = peakValue - leftMin;
	const prominence_right = peakValue - rightMin;
	const prominence_combined = Math.min(prominence_left, prominence_right);
	return {prominence_combined, prominence_left, prominence_right};
}